import { XRPC, XRPCError } from '@atcute/client';
import type {
  AestheticsRecord,
  ActorProfile,
  AtprotoBlobRef,
  AuthorFeedResponse,
  ChatConversation,
  ChatConversationListResponse,
  ChatMessage,
  ChatMessagesResponse,
  ContentRecord,
  FeedPost,
  NotificationListResponse,
  PostLikesResponse,
  TimelineResponse,
} from '../domain/types';
import { blueskyAppViewHeaders } from './appview';

function rkeyFromUri(uri: string): string {
  const rkey = uri.split('/').pop();
  if (!rkey) throw new Error('This record URI is invalid.');
  return rkey;
}

function bounded(value: unknown, max: number): string {
  return String(value ?? '').trim().slice(0, max);
}

function safeUrl(value: unknown): string {
  const input = bounded(value, 2048);
  if (!input) return '';
  try {
    const url = new URL(input);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : '';
  } catch {
    return '';
  }
}

function safeColour(value: unknown, fallback: string): string {
  const input = bounded(value, 64);
  return /^#[0-9a-f]{3,8}$/i.test(input) ? input : fallback;
}

export async function getAuthenticatedProfile(
  rpc: XRPC,
  actor: string,
): Promise<ActorProfile> {
  const response = await rpc.request({
    type: 'get',
    nsid: 'app.bsky.actor.getProfile',
    headers: blueskyAppViewHeaders(),
    params: { actor },
  });
  return response.data as ActorProfile;
}

export async function createRecord(
  rpc: XRPC,
  repo: string,
  collection: string,
  record: Record<string, unknown>,
  validate?: boolean,
): Promise<{ uri: string; cid: string }> {
  const response = await rpc.request({
    type: 'post',
    nsid: 'com.atproto.repo.createRecord',
    data: {
      repo,
      collection,
      record,
      ...(validate === false ? { validate: false } : {}),
    },
  });
  return response.data as { uri: string; cid: string };
}

export async function deleteRecord(rpc: XRPC, repo: string, uri: string): Promise<void> {
  const collection = uri.split('/').at(-2);
  if (!collection) throw new Error('This record URI is invalid.');
  await rpc.request({
    type: 'post',
    nsid: 'com.atproto.repo.deleteRecord',
    data: { repo, collection, rkey: rkeyFromUri(uri) },
  });
}

export async function setFollowing(
  rpc: XRPC,
  repo: string,
  subjectDid: string,
  existingUri?: string,
): Promise<string | undefined> {
  if (existingUri) {
    await deleteRecord(rpc, repo, existingUri);
    return undefined;
  }
  const result = await createRecord(rpc, repo, 'app.bsky.graph.follow', {
    $type: 'app.bsky.graph.follow',
    subject: subjectDid,
    createdAt: new Date().toISOString(),
  });
  return result.uri;
}

export async function toggleReaction(
  rpc: XRPC,
  repo: string,
  post: FeedPost,
  type: 'like' | 'repost',
  existingUri?: string,
): Promise<string | undefined> {
  if (existingUri) {
    await deleteRecord(rpc, repo, existingUri);
    return undefined;
  }
  const collection = `app.bsky.feed.${type}`;
  const result = await createRecord(rpc, repo, collection, {
    $type: collection,
    subject: { uri: post.uri, cid: post.cid },
    createdAt: new Date().toISOString(),
  });
  return result.uri;
}

export async function createReply(
  rpc: XRPC,
  repo: string,
  post: FeedPost,
  text: string,
): Promise<void> {
  const value = text.trim().slice(0, 300);
  if (!value) throw new Error('Write a reply first.');
  await createRecord(rpc, repo, 'app.bsky.feed.post', {
    $type: 'app.bsky.feed.post',
    text: value,
    createdAt: new Date().toISOString(),
    reply: {
      root: post.record.reply?.root || { uri: post.uri, cid: post.cid },
      parent: { uri: post.uri, cid: post.cid },
    },
  });
}

export async function createPost(
  rpc: XRPC,
  repo: string,
  text: string,
): Promise<void> {
  const value = text.trim().slice(0, 300);
  if (!value) throw new Error('Write a post first.');
  await createRecord(rpc, repo, 'app.bsky.feed.post', {
    $type: 'app.bsky.feed.post',
    text: value,
    createdAt: new Date().toISOString(),
  });
}

export async function getAuthenticatedAuthorFeed(
  rpc: XRPC,
  actor: string,
  limit = 100,
): Promise<AuthorFeedResponse> {
  const response = await rpc.request({
    type: 'get',
    nsid: 'app.bsky.feed.getAuthorFeed',
    headers: blueskyAppViewHeaders(),
    params: {
      actor,
      limit: Math.min(Math.max(limit, 1), 100),
      filter: 'posts_no_replies',
    },
  });
  return response.data as AuthorFeedResponse;
}

export async function getTimelinePage(
  rpc: XRPC,
  cursor?: string,
  limit = 100,
): Promise<TimelineResponse> {
  const response = await rpc.request({
    type: 'get',
    nsid: 'app.bsky.feed.getTimeline',
    headers: blueskyAppViewHeaders(),
    params: {
      limit: Math.min(Math.max(limit, 1), 100),
      ...(cursor ? { cursor } : {}),
    },
  });
  return response.data as TimelineResponse;
}

export async function getFollowerPage(
  rpc: XRPC,
  actor: string,
  cursor?: string,
  limit = 100,
): Promise<{ followers: ActorProfile[]; cursor?: string }> {
  const response = await rpc.request({
    type: 'get',
    nsid: 'app.bsky.graph.getFollowers',
    headers: blueskyAppViewHeaders(),
    params: {
      actor,
      limit: Math.min(Math.max(limit, 1), 100),
      ...(cursor ? { cursor } : {}),
    },
  });
  return response.data as { followers: ActorProfile[]; cursor?: string };
}

export async function getFollowingPage(
  rpc: XRPC,
  actor: string,
  cursor?: string,
  limit = 100,
): Promise<{ follows: ActorProfile[]; cursor?: string }> {
  const response = await rpc.request({
    type: 'get',
    nsid: 'app.bsky.graph.getFollows',
    headers: blueskyAppViewHeaders(),
    params: {
      actor,
      limit: Math.min(Math.max(limit, 1), 100),
      ...(cursor ? { cursor } : {}),
    },
  });
  return response.data as { follows: ActorProfile[]; cursor?: string };
}

export async function blockActor(
  rpc: XRPC,
  repo: string,
  subjectDid: string,
): Promise<string> {
  const result = await createRecord(rpc, repo, 'app.bsky.graph.block', {
    $type: 'app.bsky.graph.block',
    subject: subjectDid,
    createdAt: new Date().toISOString(),
  });
  return result.uri;
}

export async function getUnreadNotificationCount(rpc: XRPC): Promise<number> {
  const response = await rpc.request({
    type: 'get',
    nsid: 'app.bsky.notification.getUnreadCount',
    headers: blueskyAppViewHeaders(),
    params: {},
  });
  return Number((response.data as { count?: number }).count || 0);
}

export async function getNotificationPage(
  rpc: XRPC,
  cursor?: string,
  limit = 50,
  reasons?: string[],
): Promise<NotificationListResponse> {
  const response = await rpc.request({
    type: 'get',
    nsid: 'app.bsky.notification.listNotifications',
    headers: blueskyAppViewHeaders(),
    params: {
      limit: Math.min(Math.max(limit, 1), 100),
      ...(cursor ? { cursor } : {}),
      ...(reasons?.length ? { reasons } : {}),
    },
  });
  return response.data as NotificationListResponse;
}

export async function getRecentFriendRequests(
  rpc: XRPC,
  now = new Date(),
  maximumAgeDays = 30,
): Promise<ActorProfile[]> {
  const cutoff = now.getTime() - maximumAgeDays * 24 * 60 * 60 * 1_000;
  const people = new Map<string, ActorProfile>();
  let cursor: string | undefined;

  do {
    const page = await getNotificationPage(rpc, cursor, 100, ['follow']);
    let reachedCutoff = false;
    for (const notification of page.notifications || []) {
      const indexedAt = notification.indexedAt
        ? new Date(notification.indexedAt).getTime()
        : Number.NaN;
      if (!Number.isFinite(indexedAt) || indexedAt < cutoff) {
        reachedCutoff = true;
        continue;
      }
      const person = notification.author;
      if (
        person?.did
        && person.viewer?.followedBy
        && !person.viewer?.following
        && !person.viewer?.blocking
        && !person.viewer?.blockedBy
        && !people.has(person.did)
      ) {
        people.set(person.did, person);
      }
    }
    cursor = reachedCutoff ? undefined : page.cursor;
  } while (cursor);

  return [...people.values()];
}

export async function markNotificationsSeen(rpc: XRPC, seenAt = new Date()): Promise<void> {
  await rpc.request({
    type: 'post',
    nsid: 'app.bsky.notification.updateSeen',
    headers: blueskyAppViewHeaders(),
    data: { seenAt: seenAt.toISOString() },
  });
}

function chatRpc(rpc: XRPC): XRPC {
  return new XRPC({
    handler: rpc.handle,
    proxy: {
      service: 'did:web:api.bsky.chat',
      type: 'bsky_chat',
    },
  });
}

export async function getChatConversationPage(
  rpc: XRPC,
  cursor?: string,
  limit = 50,
  readState?: 'unread',
): Promise<ChatConversationListResponse> {
  const response = await chatRpc(rpc).request({
    type: 'get',
    nsid: 'chat.bsky.convo.listConvos',
    params: {
      limit: Math.min(Math.max(limit, 1), 100),
      ...(cursor ? { cursor } : {}),
      ...(readState ? { readState } : {}),
    },
  });
  return response.data as ChatConversationListResponse;
}

export async function getUnreadChatCount(rpc: XRPC): Promise<number> {
  let total = 0;
  let cursor: string | undefined;
  do {
    const page = await getChatConversationPage(rpc, cursor, 100, 'unread');
    total += (page.convos || []).reduce(
      (sum, conversation) => sum + Number(conversation.unreadCount || 0),
      0,
    );
    cursor = page.cursor;
  } while (cursor);
  return total;
}

export async function getChatMessages(
  rpc: XRPC,
  convoId: string,
  cursor?: string,
  limit = 50,
): Promise<ChatMessagesResponse> {
  const response = await chatRpc(rpc).request({
    type: 'get',
    nsid: 'chat.bsky.convo.getMessages',
    params: {
      convoId,
      limit: Math.min(Math.max(limit, 1), 100),
      ...(cursor ? { cursor } : {}),
    },
  });
  return response.data as ChatMessagesResponse;
}

export async function sendChatMessage(
  rpc: XRPC,
  convoId: string,
  text: string,
): Promise<ChatMessage> {
  const value = text.trim().slice(0, 1_000);
  if (!value) throw new Error('Write a message first.');
  const response = await chatRpc(rpc).request({
    type: 'post',
    nsid: 'chat.bsky.convo.sendMessage',
    data: {
      convoId,
      message: { text: value },
    },
  });
  return response.data as ChatMessage;
}

export async function markChatConversationRead(
  rpc: XRPC,
  convoId: string,
  messageId?: string,
): Promise<ChatConversation> {
  const response = await chatRpc(rpc).request({
    type: 'post',
    nsid: 'chat.bsky.convo.updateRead',
    data: {
      convoId,
      ...(messageId ? { messageId } : {}),
    },
  });
  return (response.data as { convo: ChatConversation }).convo;
}

export async function getChatConversationForMember(
  rpc: XRPC,
  memberDid: string,
): Promise<ChatConversation> {
  const response = await chatRpc(rpc).request({
    type: 'get',
    nsid: 'chat.bsky.convo.getConvoForMembers',
    params: { members: [memberDid] },
  });
  return (response.data as { convo: ChatConversation }).convo;
}

export async function getPostLikers(
  rpc: XRPC,
  uri: string,
  limit = 25,
): Promise<PostLikesResponse['likes']> {
  const response = await rpc.request({
    type: 'get',
    nsid: 'app.bsky.feed.getLikes',
    headers: blueskyAppViewHeaders(),
    params: { uri, limit: Math.min(Math.max(limit, 1), 100) },
  });
  return (response.data as PostLikesResponse).likes || [];
}

export async function uploadBackgroundImage(
  rpc: XRPC,
  image: Blob,
): Promise<AtprotoBlobRef> {
  const supportedTypes = new Set([
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',
  ]);
  if (!supportedTypes.has(image.type)) {
    throw new Error('Choose a PNG, GIF, JPG or WebP image.');
  }
  if (image.size > 10 * 1024 * 1024) {
    throw new Error('Background images must be no larger than 10 MB.');
  }
  const response = await rpc.request({
    type: 'post',
    nsid: 'com.atproto.repo.uploadBlob',
    data: image,
  });
  return (response.data as { blob: AtprotoBlobRef }).blob;
}

async function putRecord(
  rpc: XRPC,
  repo: string,
  collection: string,
  rkey: string,
  record: Record<string, unknown>,
): Promise<void> {
  await rpc.request({
    type: 'post',
    nsid: 'com.atproto.repo.putRecord',
    data: {
      repo,
      collection,
      rkey,
      record,
      validate: false,
    },
  });
}

export async function saveBlueskyDescription(
  rpc: XRPC,
  repo: string,
  description: string,
): Promise<void> {
  let current: {
    cid?: string;
    value?: Record<string, unknown>;
  } = {};
  try {
    const response = await rpc.request({
      type: 'get',
      nsid: 'com.atproto.repo.getRecord',
      params: {
        repo,
        collection: 'app.bsky.actor.profile',
        rkey: 'self',
      },
    });
    current = response.data as typeof current;
  } catch (reason) {
    const missing = reason instanceof XRPCError
      && (reason.status === 404 || reason.kind === 'RecordNotFound');
    if (!missing) throw reason;
    // A profile record can be absent on a new account; putRecord creates it.
  }
  await rpc.request({
    type: 'post',
    nsid: 'com.atproto.repo.putRecord',
    data: {
      repo,
      collection: 'app.bsky.actor.profile',
      rkey: 'self',
      record: {
        ...(current.value || {}),
        $type: 'app.bsky.actor.profile',
        description: bounded(description, 256),
      },
      ...(current.cid ? { swapRecord: current.cid } : {}),
    },
  });
}

export async function saveSkyspaceProfile(
  rpc: XRPC,
  repo: string,
  content: ContentRecord,
  aesthetics: AestheticsRecord,
  existing?: {
    contentUri?: string;
    aestheticsUri?: string;
  },
): Promise<void> {
  await Promise.all([
    saveSkyspaceContent(rpc, repo, content, existing?.contentUri),
    saveSkyspaceAesthetics(rpc, repo, aesthetics, existing?.aestheticsUri),
  ]);
}

export async function resetSkyspacePage(
  rpc: XRPC,
  repo: string,
  existing?: {
    contentUri?: string;
    aestheticsUri?: string;
  },
): Promise<void> {
  const createdAt = new Date().toISOString();
  await Promise.all([
    putRecord(
      rpc,
      repo,
      'me.skyspace.content',
      existing?.contentUri ? rkeyFromUri(existing.contentUri) : 'profile',
      {
        $type: 'me.skyspace.content',
        wiltm: '',
        music: '',
        top8: '',
        interests: '',
        createdAt,
      },
    ),
    putRecord(
      rpc,
      repo,
      'me.skyspace.aesthetics',
      existing?.aestheticsUri ? rkeyFromUri(existing.aestheticsUri) : 'profile',
      {
        $type: 'me.skyspace.aesthetics',
        reset: true,
        createdAt,
      },
    ),
  ]);
}

export async function saveSkyspaceContent(
  rpc: XRPC,
  repo: string,
  content: ContentRecord,
  existingUri?: string,
): Promise<void> {
  const createdAt = new Date().toISOString();
  const safeContent: ContentRecord = {};
  if (content.wiltm !== undefined) safeContent.wiltm = bounded(content.wiltm, 3000);
  if (content.music !== undefined) safeContent.music = bounded(content.music, 2048);
  if (content.top8 !== undefined) safeContent.top8 = bounded(content.top8, 2048);
  if (content.interests !== undefined) safeContent.interests = bounded(content.interests, 5000);
  if (content.customPanels !== undefined) {
    const cleanGameUrl = content.customPanels.some(panel => panel.type === 'game')
      ? (await import('../domain/game-embed')).gameEmbedUrl
      : undefined;
    const seen = new Set<string>();
    safeContent.customPanels = content.customPanels.slice(0, 3).flatMap(panel => {
      const id = bounded(panel?.id, 24);
      if (!/^[A-Za-z0-9_-]+$/.test(id) || seen.has(id)) return [];
      seen.add(id);
      const type = (
        panel.type === 'image'
        || panel.type === 'video'
        || panel.type === 'game'
        || panel.type === 'chat'
        || panel.type === 'graffiti'
      ) ? panel.type : 'text';
      return [{
        id,
        title: bounded(panel.title, 80),
        type,
        content: type === 'chat' || type === 'graffiti'
          ? ''
          : type === 'text'
          ? bounded(panel.content, 5000)
          : type === 'game'
            ? cleanGameUrl?.(panel.content) || ''
            : safeUrl(panel.content),
      }];
    });
  }
  await putRecord(
    rpc,
    repo,
    'me.skyspace.content',
    existingUri ? rkeyFromUri(existingUri) : 'profile',
    { ...safeContent, $type: 'me.skyspace.content', createdAt },
  );
  await Promise.all((safeContent.customPanels || [])
    .filter(panel => panel.type === 'chat' || panel.type === 'graffiti')
    .map(panel => putRecord(
      rpc,
      repo,
      panel.type === 'chat'
        ? 'me.skyspace.chat.room'
        : 'me.skyspace.graffiti.wall',
      panel.id,
      {
        $type: panel.type === 'chat'
          ? 'me.skyspace.chat.room'
          : 'me.skyspace.graffiti.wall',
        title: panel.title,
        createdAt,
      },
    )));
}

export async function saveSkyspaceAesthetics(
  rpc: XRPC,
  repo: string,
  aesthetics: AestheticsRecord,
  existingUri?: string,
): Promise<void> {
  const createdAt = new Date().toISOString();
  let shaderPreview = aesthetics.bg?.preview;
  if (
    aesthetics.bg?.source === 'shader'
    && !shaderPreview
    && existingUri
  ) {
    try {
      const response = await rpc.request({
        type: 'get',
        nsid: 'com.atproto.repo.getRecord',
        params: {
          repo,
          collection: 'me.skyspace.aesthetics',
          rkey: rkeyFromUri(existingUri),
        },
      });
      const current = response.data as { value?: AestheticsRecord };
      const currentBackground = current.value?.bg;
      const shaderIdentity = (
        shader: NonNullable<AestheticsRecord['bg']>['shader'],
      ) => JSON.stringify({
        preset: shader?.preset || '',
        code: shader?.code || '',
        attribution: shader?.attribution || '',
        attributionUrl: shader?.attributionUrl || '',
      });
      if (
        currentBackground?.preview
        && shaderIdentity(currentBackground.shader) === shaderIdentity(aesthetics.bg.shader)
      ) {
        shaderPreview = currentBackground.preview;
      }
    } catch (reason) {
      const missing = reason instanceof XRPCError
        && (reason.status === 404 || reason.kind === 'RecordNotFound');
      if (!missing) throw reason;
    }
  }
  const safeAesthetics: AestheticsRecord = {};
  if (aesthetics.bg) {
    const source = ['upload', 'url', 'colour', 'shader'].includes(aesthetics.bg.source || '')
      ? aesthetics.bg.source
      : undefined;
    const colorMode = ['solid', 'pattern', 'gradient', 'cycle']
      .includes(aesthetics.bg.colorMode || '')
      ? aesthetics.bg.colorMode
      : undefined;
    const pattern = aesthetics.bg.pattern === 'checks' ? 'checks'
      : aesthetics.bg.pattern === 'stripes' ? 'stripes' : undefined;
    const colors = aesthetics.bg.colors
      ?.slice(0, 6)
      .map(value => safeColour(value, ''))
      .filter(Boolean);
    const parsedAngle = Number(aesthetics.bg.gradientAngle);
    const shaderPreset = bounded(aesthetics.bg.shader?.preset, 40);
    const shaderCode = bounded(aesthetics.bg.shader?.code, 50_000);
    const shaderAttribution = bounded(aesthetics.bg.shader?.attribution, 160);
    const shaderAttributionUrl = safeUrl(aesthetics.bg.shader?.attributionUrl);
    safeAesthetics.bg = {
      url: safeUrl(aesthetics.bg?.url),
      ...(aesthetics.bg.image ? { image: aesthetics.bg.image } : {}),
      ...(shaderPreview ? { preview: shaderPreview } : {}),
      color: safeColour(aesthetics.bg?.color, '#ffffff'),
      position: bounded(aesthetics.bg?.position, 40),
      ...(source ? { source } : {}),
      ...(colorMode ? { colorMode } : {}),
      ...(typeof aesthetics.bg.colorCycle === 'boolean'
        ? { colorCycle: aesthetics.bg.colorCycle }
        : {}),
      ...(Number.isFinite(Number(aesthetics.bg.colorCycleSpeed))
        ? {
          colorCycleSpeed: Math.max(
            0,
            Math.min(100, Math.round(Number(aesthetics.bg.colorCycleSpeed))),
          ),
        }
        : {}),
      ...(pattern ? { pattern } : {}),
      ...(colors?.length ? { colors } : {}),
      ...(Number.isFinite(parsedAngle)
        ? { gradientAngle: Math.max(0, Math.min(360, Math.round(parsedAngle))) }
        : {}),
      ...(source === 'shader' && shaderPreset
        ? {
          shader: {
            preset: shaderPreset,
            ...(shaderPreset === 'custom' && shaderCode ? { code: shaderCode } : {}),
            ...(shaderAttribution ? { attribution: shaderAttribution } : {}),
            ...(shaderAttributionUrl ? { attributionUrl: shaderAttributionUrl } : {}),
          },
        }
        : {}),
    };
  }
  if (aesthetics.panel) {
    safeAesthetics.panel = {
      frameThickness: bounded(aesthetics.panel?.frameThickness, 10),
      frameRadius: bounded(aesthetics.panel?.frameRadius, 10),
      frameStyle: bounded(aesthetics.panel?.frameStyle, 20),
      frameColor: safeColour(aesthetics.panel?.frameColor, '#ffffff'),
      panelColor: safeColour(aesthetics.panel?.panelColor, '#ffffff'),
      panelTransparency: bounded(aesthetics.panel?.panelTransparency, 10),
      seperatorColor: safeColour(aesthetics.panel?.seperatorColor, '#ffcb99'),
    };
  }
  if (aesthetics.text) {
    safeAesthetics.text = {
      font: bounded(aesthetics.text?.font, 80),
      bodyColor: safeColour(aesthetics.text?.bodyColor, '#000000'),
      titleColor: safeColour(aesthetics.text?.titleColor, '#ff5800'),
      linkColor: safeColour(aesthetics.text?.linkColor, '#002b99'),
    };
  }
  if (aesthetics.bio) {
    safeAesthetics.bio = {
      quote: bounded(aesthetics.bio?.quote, 1000),
      location: bounded(aesthetics.bio?.location, 500),
    };
  }
  if (aesthetics.effects) {
    safeAesthetics.effects = {
      cursorImage: bounded(aesthetics.effects?.cursorImage, 80),
      cursorURL: safeUrl(aesthetics.effects?.cursorURL),
      cursorTrail: bounded(aesthetics.effects?.cursorTrail, 40),
      cursorTrailParameters: bounded(aesthetics.effects?.cursorTrailParameters, 1000),
    };
  }
  if (aesthetics.layout) {
    const standard = new Set([
      'latest',
      'blurbs',
      'music',
      'friends',
      'comments',
      'replies',
      'interests',
    ]);
    const allowed = (value: string) => standard.has(value)
      || /^custom:[A-Za-z0-9_-]{1,24}$/.test(value);
    const seen = new Set<string>();
    const clean = (values?: string[]) => (values || []).flatMap(value => {
      if (!allowed(value) || seen.has(value)) return [];
      seen.add(value);
      return [value];
    });
    safeAesthetics.layout = {
      left: clean(aesthetics.layout.left),
      right: clean(aesthetics.layout.right),
      hidden: [...new Set(aesthetics.layout.hidden)]
        .filter(allowed),
    };
  }
  await putRecord(
    rpc,
    repo,
    'me.skyspace.aesthetics',
    existingUri ? rkeyFromUri(existingUri) : 'profile',
    { ...safeAesthetics, $type: 'me.skyspace.aesthetics', createdAt },
  );
}
